import React from 'react';
import { Navigate } from 'react-router-dom';
import { PrivateRoute } from 'shared/components/PrivateRoute';
import { paths, routeSegments } from 'shared/routing/paths';
import { ExpertLayout } from 'features/expert/components/ExpertLayout';
import { OnboardingLayout } from 'features/expert/components/OnboardingLayout';
import { defineRoute, AppRoute } from './types';
import { lazyNamed } from './lazy';

const OnboardingAccountPage = lazyNamed(
  () => import('features/expert/pages/OnboardingAccountPage'),
  'OnboardingAccountPage',
);
const OnboardingExpertisePage = lazyNamed(
  () => import('features/expert/pages/OnboardingExpertisePage'),
  'OnboardingExpertisePage',
);
const OnboardingHourlyRatePage = lazyNamed(
  () => import('features/expert/pages/OnboardingHourlyRatePage'),
  'OnboardingHourlyRatePage',
);
const OnboardingIdVerificationPage = lazyNamed(
  () => import('features/expert/pages/OnboardingIdVerificationPage'),
  'OnboardingIdVerificationPage',
);
const DevSmokePage = lazyNamed(() => import('features/expert/pages/DevSmokePage'), 'DevSmokePage');
const ExpertHomePage = lazyNamed(() => import('features/expert/pages/ExpertHomePage'), 'ExpertHomePage');
const AssignedTasksPage = lazyNamed(() => import('features/expert/pages/AssignedTasksPage'), 'AssignedTasksPage');
const CampaignsListPage = lazyNamed(() => import('features/expert/pages/CampaignsListPage'), 'CampaignsListPage');
const CampaignDetailPage = lazyNamed(() => import('features/expert/pages/CampaignDetailPage'), 'CampaignDetailPage');
const CampaignTimesheetPage = lazyNamed(
  () => import('features/expert/pages/CampaignTimesheetPage'),
  'CampaignTimesheetPage',
);
const ExpertEarningsPage = lazyNamed(() => import('features/expert/pages/ExpertEarningsPage'), 'ExpertEarningsPage');
const TaskContainerPage = lazyNamed(() => import('features/expert/pages/TaskContainerPage'), 'TaskContainerPage');
const JobInstructionsPage = lazyNamed(() => import('features/expert/pages/JobInstructionsPage'), 'JobInstructionsPage');
const CompletedJobSummaryPage = lazyNamed(
  () => import('features/expert/pages/CompletedJobSummaryPage'),
  'CompletedJobSummaryPage',
);
const ExpertAnalyticsPage = lazyNamed(() => import('features/expert/pages/ExpertAnalyticsPage'), 'ExpertAnalyticsPage');
const MessageBoardPage = lazyNamed(() => import('features/expert/pages/MessageBoardPage'), 'MessageBoardPage');
const KnowledgeBasePage = lazyNamed(() => import('features/expert/pages/KnowledgeBasePage'), 'KnowledgeBasePage');
const KnowledgeBaseItemPage = lazyNamed(
  () => import('features/expert/pages/KnowledgeBaseItemPage'),
  'KnowledgeBaseItemPage',
);
const ExpertTrainingPage = lazyNamed(() => import('features/expert/pages/ExpertTrainingPage'), 'ExpertTrainingPage');
const PromptTesting2Page = lazyNamed(() => import('features/expert/pages/PromptTesting2Page'), 'PromptTesting2Page');
const ThirdPartyProxyPage = lazyNamed(() => import('features/expert/pages/ThirdPartyProxyPage'), 'ThirdPartyProxyPage');
const PartnerAccessPage = lazyNamed(() => import('features/expert/pages/PartnerAccessPage'), 'PartnerAccessPage');
const WideSearchPage = lazyNamed(() => import('features/expert/pages/WideSearchPage'), 'WideSearchPage');
const SettingsPersonalPage = lazyNamed(
  () => import('features/expert/pages/SettingsPersonalPage'),
  'SettingsPersonalPage',
);
const SettingsProfessionalPage = lazyNamed(
  () => import('features/expert/pages/SettingsProfessionalPage'),
  'SettingsProfessionalPage',
);
const SettingsPaymentsPage = lazyNamed(
  () => import('features/expert/pages/SettingsPaymentsPage'),
  'SettingsPaymentsPage',
);
const NdaAgreementPage = lazyNamed(() => import('features/expert/pages/NdaAgreementPage'), 'NdaAgreementPage');
const NdaViewerPage = lazyNamed(() => import('features/expert/pages/NdaViewerPage'), 'NdaViewerPage');

const expertRootId = 'expert.shell';

function expertChild(
  id: string,
  path: string,
  element: React.ReactNode,
  layout: 'shell' | 'full-width' | 'full-height' = 'shell',
  redirectTo?: string,
): AppRoute {
  return defineRoute(
    {
      id,
      path,
      area: 'expert',
      guard: 'inherited',
      layout,
      kind: redirectTo ? 'redirect' : 'page',
      parentId: expertRootId,
      ...(redirectTo ? { redirectTo } : {}),
    },
    path === paths.expert.root ? { index: true, element } : { path: path.slice(paths.expert.root.length + 1), element },
  );
}

const onboardingRootId = 'expert.onboarding';
const onboardingRoute = defineRoute(
  {
    id: onboardingRootId,
    path: paths.onboarding.root,
    area: 'expert',
    guard: 'private',
    layout: 'shell',
    kind: 'layout',
  },
  {
    path: paths.onboarding.root,
    element: (
      <PrivateRoute requireVerifiedEmail>
        <OnboardingLayout />
      </PrivateRoute>
    ),
    children: [
      defineRoute(
        {
          id: 'expert.onboarding.account',
          path: paths.onboarding.root,
          area: 'expert',
          guard: 'inherited',
          layout: 'shell',
          kind: 'page',
          parentId: onboardingRootId,
        },
        { index: true, element: <OnboardingAccountPage /> },
      ),
      defineRoute(
        {
          id: 'expert.onboarding.expertise',
          path: paths.onboarding.expertise,
          area: 'expert',
          guard: 'inherited',
          layout: 'shell',
          kind: 'page',
          parentId: onboardingRootId,
        },
        { path: routeSegments.onboarding.expertise, element: <OnboardingExpertisePage /> },
      ),
      defineRoute(
        {
          id: 'expert.onboarding.hourlyRate',
          path: paths.onboarding.hourlyRate,
          area: 'expert',
          guard: 'inherited',
          layout: 'shell',
          kind: 'page',
          parentId: onboardingRootId,
        },
        { path: routeSegments.onboarding.hourlyRate, element: <OnboardingHourlyRatePage /> },
      ),
      defineRoute(
        {
          id: 'expert.onboarding.idVerification',
          path: paths.onboarding.idVerification,
          area: 'expert',
          guard: 'inherited',
          layout: 'shell',
          kind: 'page',
          parentId: onboardingRootId,
        },
        { path: routeSegments.onboarding.idVerification, element: <OnboardingIdVerificationPage /> },
      ),
    ],
  },
);

const expertShellRoute = defineRoute(
  {
    id: expertRootId,
    path: paths.expert.root,
    area: 'expert',
    guard: 'expert',
    layout: 'shell',
    kind: 'layout',
  },
  {
    path: paths.expert.root,
    element: (
      <PrivateRoute requireRole="expert" requireVerifiedEmail requireOnboarded>
        <ExpertLayout />
      </PrivateRoute>
    ),
    children: [
      expertChild('expert.home', paths.expert.root, <ExpertHomePage />),
      expertChild('expert.assigned', paths.expert.assigned, <AssignedTasksPage />),
      expertChild('expert.campaigns', paths.expert.campaigns, <CampaignsListPage />),
      expertChild(
        'expert.campaignDetail',
        paths.expert.campaigns + '/:campaignId',
        <CampaignDetailPage />,
        'full-width',
      ),
      expertChild(
        'expert.campaignTimesheet',
        paths.expert.campaigns + '/:campaignId/timesheet',
        <CampaignTimesheetPage />,
        'full-width',
      ),
      expertChild('expert.earnings', paths.expert.earnings, <ExpertEarningsPage />, 'full-width'),
      expertChild('expert.reviewTasks', paths.expert.reviewTasks, <TaskContainerPage />, 'full-height'),
      expertChild('expert.reviewTask', paths.expert.reviewTasks + '/:taskId', <TaskContainerPage />, 'full-height'),
      expertChild('expert.jobSummary', paths.expert.jobSummary, <CompletedJobSummaryPage />),
      expertChild(
        'expert.jobInstructions',
        paths.expert.root + '/job-instructions/:jobInstructionsId',
        <JobInstructionsPage />,
      ),
      expertChild('expert.analytics', paths.expert.analytics, <ExpertAnalyticsPage />),
      expertChild('expert.messageBoard', paths.expert.messageBoard, <MessageBoardPage />, 'full-width'),
      expertChild('expert.knowledgeBase', paths.expert.knowledgeBase, <KnowledgeBasePage />),
      expertChild('expert.knowledgeBaseItem', paths.expert.knowledgeBase + '/:articleId', <KnowledgeBaseItemPage />),
      expertChild('expert.training', paths.expert.training, <ExpertTrainingPage />),
      expertChild('expert.promptTesting2', paths.expert.promptTesting2, <PromptTesting2Page />),
      expertChild('expert.thirdPartyProxy', paths.expert.thirdPartyProxy, <ThirdPartyProxyPage />),
      expertChild(
        'expert.setUpProxy',
        paths.expert.setUpProxy,
        <Navigate to={paths.expert.thirdPartyProxy} replace />,
        'shell',
        paths.expert.thirdPartyProxy,
      ),
      expertChild('expert.partnerAccess', paths.expert.partnerAccess, <PartnerAccessPage />),
      expertChild('expert.wideSearch', paths.expert.wideSearch, <WideSearchPage />),
      expertChild('expert.settingsPersonal', paths.expert.settingsPersonal, <SettingsPersonalPage />),
      expertChild('expert.settingsProfessional', paths.expert.settingsProfessional, <SettingsProfessionalPage />),
      expertChild('expert.settingsPayments', paths.expert.settingsPayments, <SettingsPaymentsPage />),
      expertChild('expert.settingsNdaAgreement', paths.expert.settingsNdaAgreement, <NdaAgreementPage />),
      expertChild('expert.settingsNdaViewer', paths.expert.settingsNdaViewer, <NdaViewerPage />),
    ],
  },
);

const devSmokeRoute = defineRoute(
  {
    id: 'expert.devSmoke',
    path: paths.public.devSmoke,
    area: 'expert',
    guard: 'expert',
    layout: 'public',
    kind: 'page',
  },
  {
    path: paths.public.devSmoke,
    element: (
      <PrivateRoute requireRole="expert" requireVerifiedEmail requireOnboarded>
        <DevSmokePage />
      </PrivateRoute>
    ),
  },
);

export const EXPERT_ROUTES: AppRoute[] = [onboardingRoute, devSmokeRoute, expertShellRoute];
